<template>
  <div class="purchase-invoice-container">
    <div v-if="loading">
      <p>Loading page...</p>
    </div>
    <div v-if="error" class="error-alert" role="alert">
      <span class="error-icon">⚠️</span>
      <span class="error-message"><strong>Error:</strong> {{ error }}</span>
    </div>
    <div v-if="successMessage" class="success-alert" role="status">
      <span class="success-icon">✅</span>
      <span class="success-message">{{ successMessage }}</span>
    </div>
    <InvoiceDetails v-else-if="invoice" :invoice="invoice" :accounts="accounts" :dimensions="dimensions"
      :vatCodes="vatCodes" @approve="approve" @reject="reject" @update="update" @updateInvoice="handleInvoiceUpdate" />
        <!-- Loading overlay for submitting -->
  <div v-if="submitting" class="submitting-overlay">
    <div class="submitting-overlay-content">
      <span class="spinner"></span>
      <span>Processing...</span>
    </div>
  </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useAuthStore } from '@/store/AuthStore';
import router from '@/router';
import { AxiosError } from 'axios';
import { Account, Dimension, PurchaseInvoice, VatCode } from "@/types/invoice";
import { invoiceService } from '@/services/invoiceService';
import InvoiceDetails from "@/components/Invoice.vue";
import { User } from '@/types/user';
import { userService } from '@/services/userService';


export default defineComponent({
  name: 'PurchaseInvoice',
  components: { InvoiceDetails },
  setup() {
    const authStore = useAuthStore(); // Get the auth store instance

    // State
    const loading = ref<boolean>(true);
    const error = ref<string | null>(null);
    const invoice = ref<PurchaseInvoice | null>(null);
    const accounts = ref<Account[]>([]);
    const dimensions = ref<Dimension[]>([]);
    const vatCodes = ref<VatCode[]>([]);
    const users = ref<User[]>([]);
    const submitting = ref<boolean>(false);
    const changesPending = ref<boolean>(false);
    const successMessage = ref<string | null>(null);

    // Computed properties ->
    const invoiceLineTotal = computed((): number => {
      if (!invoice.value || !invoice.value.invoiceLines) return 0;
      return invoice.value.invoiceLines.reduce((sum, line) => sum + line.lineSum, 0);
    });

    // Methods ->

    // Fetch invoice data from backend in one call
    const fetchInvoiceData = async (): Promise<void> => {
      try {
        loading.value = true;
        const response = await invoiceService.getInvoiceDataCombined();
        if (response.data?.data?.noMoreInvoices) {
          successMessage.value = 'No more invoices to process.';
           loading.value = false;
          return;
        }
        invoice.value = response.data.data.invoice;
        accounts.value = response.data.data.accounts;
        dimensions.value = response.data.data.dimensions;
        vatCodes.value = response.data.data.taxCodes;
      } catch (err) {
        handleError(err);
      } finally {
        loading.value = false;
      }
    };

    // Show success message and fetch next invoice after a delay
    const showSuccessAndNext = async (msg: string): Promise<void> => {
      successMessage.value = msg;
      submitting.value = true;
      await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 seconds
     
      try {
        const response = await invoiceService.getInvoiceDataCombined();
        if (response.data?.data?.noMoreInvoices) {
          successMessage.value = 'No more invoices to process.';
          submitting.value = false;
          return;
        }
        successMessage.value = null;
        invoice.value = response.data.data.invoice;
        accounts.value = response.data.data.accounts;
        dimensions.value = response.data.data.dimensions;
        vatCodes.value = response.data.data.taxCodes;
      } catch (err) {
        handleError(err);
      } finally {
        submitting.value = false;
      }
    };

    const fetchUsers = async (): Promise<void> => {
      try {
        loading.value = true;
        const response = await userService.getUsers();
        users.value = response.data.data;
      } catch (err) {
        handleError(err);
      } finally {
        loading.value = false;
      }
    };

    const handleError = (err: Error | AxiosError | any): void => {
      console.error('Error:', err);

      // Check for authentication error
      if (err.response && err.response.status === 401) {
        error.value = 'You are not authenticated. Please log in again.';
        authStore.clearToken();
        // router.push('/unauthenticated');
      } else if (err.response && err.response.data && err.response.data.error) {
        // Use backend-provided error message if available
        error.value = err.response.data.error;
      } else {
        error.value = err.message || 'An error occurred. Please try again.';
      }
    };

    const handleInvoiceUpdate = (updatedInvoice: PurchaseInvoice): void => {
      invoice.value = JSON.parse(JSON.stringify(updatedInvoice));
      changesPending.value = true;
    };

    const validateAccountingBalance = (): void => {
      // This will automatically update the computed properties
      // No additional logic needed here as the balance warning is shown based on computed value
    };

    // Update invoice and refresh data on UI
    const update = async (): Promise<void> => {
      try {
        submitting.value = true;
        if (invoice.value) {
          await invoiceService.updateInvoice(invoice.value);
        } else {
          throw new Error('No invoice data available');
        }

        // Fetch same invoice again to refresh data
        await fetchInvoiceData();
        changesPending.value = false;
      } catch (err) {
        handleError(err);
      } finally {
        submitting.value = false;
      }
    };

    // Approve invoice and handle next steps
    const approve = async (updatedInvoice?: PurchaseInvoice): Promise<void> => {
      try {
        submitting.value = true;
        // Submit approval
        if (invoice.value) {
          // if approved, the next status is 'accepted'
          if (invoice.value.originalApprovalStatus === 'approved') {
            invoice.value.approvalStatus = 'accepted';
          } else {
            invoice.value.approvalStatus = 'approved';
          }
          const response = await invoiceService.updateInvoice(invoice.value);
          console.info(`Auto-fetch next invoice: ${invoice.value.autoFetchNextInvoice}`);
          if (response.data.data.updatedInvoice && invoice.value.autoFetchNextInvoice) {
            await showSuccessAndNext('Invoice approved! Loading next...');
            return;
          }
        } else {
          throw new Error('No invoice data available');
        }

        // Redirect to success page or list
        await router.push('/success');
      } catch (err) {
        handleError(err);
      } finally {
        submitting.value = false;
      }
    };

    const reject = async (): Promise<void> => {
      try {
        submitting.value = true;

        // Submit rejection
        if (invoice.value) {
          invoice.value.approvalStatus = 'rejected';
          const response = await invoiceService.updateInvoice(invoice.value);
          if (response.data.success && invoice.value.autoFetchNextInvoice) {
            await showSuccessAndNext('Invoice rejected! Loading next...');
            return;
          }
        } else {
          throw new Error('No invoice data available');
        }
        await router.push('/success');
      } catch (err) {
        handleError(err);
      } finally {
        submitting.value = false;
      }

    };

    // Lifecycle hooks
    onMounted(() => {
      if (!authStore.token) {
        error.value = 'Please log in to access this page.';
        // router.push('/');
        loading.value = false;
        return;
      }
      fetchInvoiceData();
      //fetchUsers();

    });

    // Watch for token changes
    watch(() => authStore.token, (newToken) => {
      if (!newToken) {
        error.value = 'Authentication lost. Please log in again.';
        //router.push('/');
      }
    });

    return {
      loading,
      error,
      successMessage,
      invoice,
      accounts,
      dimensions,
      vatCodes,
      users,
      submitting,
      invoiceLineTotal,
      handleInvoiceUpdate,
      validateAccountingBalance,
      approve,
      reject,
      update
    };
  }
});
</script>

<style scoped>
/* Page styling */

.purchase-invoice-container {
  max-width: 100%;
  box-sizing: border-box;
}

.error-alert {
  max-width: 100%;
  display: flex;
  align-items: center;
  background-color: #ffeaea;
  color: #b71c1c;
  border: 1px solid #f44336;
  border-radius: 6px;
  padding: 1rem 1.5rem;
  margin: 1rem auto;
  font-size: 1.1rem;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.08);
  margin-top: 80px;
}

.error-icon {
  font-size: 1.5rem;
  margin-right: 0.75rem;
}

.error-message {
  flex: 1;
}

.success-alert {
  max-width: 100%;
  display: flex;
  align-items: center;
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #43a047;
  border-radius: 6px;
  padding: 1rem 1.5rem;
  margin: 1rem auto;
  font-size: 1.1rem;
  box-shadow: 0 2px 8px rgba(67, 160, 71, 0.08);
}

.success-icon {
  font-size: 1.5rem;
  margin-right: 0.75rem;
}

.success-message {
  flex: 1;
}

.submitting-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submitting-overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 1.2rem;
  color: #333;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #43a047;
  border-top: 4px solid #e8f5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>